/**
 * SchedulingIntegration — the FHL Scheduling card on /crm/integrations.
 *
 * The entire Cal.diy experience, simplified and headless: users never see
 * cal.fhl.one. Everything goes through the cal* callables (which talk to the
 * admin sidecar on the scheduling VM):
 *
 *   - Setup (first run): connect Google Calendar (shared with CRM sync,
 *     needs the readonly scope) → one-click account provisioning → booking link
 *   - Booking Pages: the moved BookingPagesManager (meeting-type dropdown,
 *     no raw cal URLs)
 *   - Meeting Types: list + editor (duration, notice, buffers; Google Meet locked)
 *   - Availability: weekly hours + timezone
 *   - Bookings: upcoming/past with attendee info, Meet link, and Cancel
 *     (real cancellation emails + webhook → CRM timeline updates)
 *
 * Gating: requires calSchedulingEnabled on the target user; admins can view
 * and manage other users (Google connect stays self-only — OAuth binds to the
 * signed-in caller).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Center,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useClipboard,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCopy,
  FiEdit2,
  FiExternalLink,
  FiPlus,
  FiRefreshCw,
  FiTrash2,
  FiUserX,
  FiVideo,
  FiXCircle,
} from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';
import BookingPagesManager from '../Settings/BookingPagesManager';

const fns = () => getFunctions(app, 'us-east1');
const call = (name, payload) => httpsCallable(fns(), name)(payload).then((r) => r.data);

const DAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
];

/** '09:00:00' -> '09:00' for <input type="time"> */
const toInputTime = (t) => String(t || '09:00').slice(0, 5);

function formatBookingTime(iso, timeZone) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timeZone || undefined,
    }).format(new Date(iso));
  } catch (e) {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Setup checklist (first run)
// ---------------------------------------------------------------------------

function SetupChecklist({ userId, isSelf, status, onDone }) {
  const toast = useToast();
  const [connecting, setConnecting] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const stepBg = useColorModeValue('gray.50', 'gray.700');

  const googleReady = status.google?.connected && status.google?.scopeOk;
  const needsReconnect = status.google?.connected && !status.google?.scopeOk;

  const handleConnectGoogle = async () => {
    setConnecting(true);
    try {
      const result = await call('initCalendarOAuth', {
        redirectUri: `${window.location.origin}/oauth/calendar/callback`,
      });
      if (!result.authUrl) throw new Error('No auth URL returned');

      const messageHandler = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data.type === 'oauth_success') {
          window.removeEventListener('message', messageHandler);
          setConnecting(false);
          toast({ title: 'Google Calendar connected', status: 'success', duration: 3000 });
          onDone(); // reload status
        } else if (event.data.type === 'oauth_error') {
          window.removeEventListener('message', messageHandler);
          setConnecting(false);
          toast({ title: 'Connection failed', description: event.data.error || 'Could not connect', status: 'error', duration: 6000 });
        }
      };
      window.addEventListener('message', messageHandler);

      const width = 600;
      const height = 700;
      window.open(
        result.authUrl,
        'GoogleCalendarOAuth',
        `width=${width},height=${height},left=${window.screen.width / 2 - width / 2},top=${window.screen.height / 2 - height / 2},noopener=no`
      );
      setTimeout(() => {
        window.removeEventListener('message', messageHandler);
        setConnecting(false);
      }, 300000);
    } catch (err) {
      setConnecting(false);
      toast({ title: 'Connection failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  const handleProvision = async () => {
    setProvisioning(true);
    try {
      const result = await call('calProvisionUser', {
        userId,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      });
      toast({
        title: 'Scheduling is ready!',
        description: `Your booking link: ${result.bookingUrl}`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
      onDone();
    } catch (err) {
      const msg = err.message || '';
      toast({
        title: 'Setup failed',
        description: msg.includes('reconnect_required')
          ? 'Your Google connection needs a quick refresh — click Reconnect in step 1 first.'
          : msg.includes('google_not_connected')
            ? 'Connect Google Calendar first (step 1).'
            : msg,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setProvisioning(false);
    }
  };

  return (
    <VStack align="stretch" spacing={4} maxW="640px">
      <Box>
        <Heading size="md" mb={1}>Set up FHL Scheduling</Heading>
        <Text fontSize="sm" color="gray.500">
          Two steps, about a minute. You get a personal booking link that plugs into
          texts, emails, and campaigns — meetings land on your Google Calendar with a
          Google Meet link automatically.
        </Text>
      </Box>

      {/* Step 1 — Google Calendar */}
      <HStack bg={stepBg} p={4} borderRadius="lg" spacing={4} align="flex-start">
        <Icon
          as={googleReady ? FiCheckCircle : FiCalendar}
          boxSize={6}
          color={googleReady ? 'green.400' : 'blue.400'}
          mt={1}
        />
        <Box flex="1">
          <HStack justify="space-between">
            <Text fontWeight="semibold">1. Connect Google Calendar</Text>
            {googleReady && <Badge colorScheme="green">Done</Badge>}
            {needsReconnect && <Badge colorScheme="yellow">Reconnect needed</Badge>}
          </HStack>
          <Text fontSize="sm" color="gray.500" mt={1}>
            {googleReady
              ? 'Connected — scheduling can check your availability and add meetings.'
              : needsReconnect
                ? 'Your calendar is connected for sync, but scheduling needs one extra permission (read free/busy). Reconnect once to grant it.'
                : 'One connection powers both CRM calendar sync and scheduling.'}
          </Text>
          {!googleReady && (
            <Tooltip
              isDisabled={isSelf}
              label="Google can only be connected by the user themselves — have them log in and connect."
            >
              <Button
                mt={3}
                size="sm"
                colorScheme="blue"
                onClick={handleConnectGoogle}
                isLoading={connecting}
                isDisabled={!isSelf}
                leftIcon={needsReconnect ? <FiRefreshCw /> : <FiCalendar />}
              >
                {needsReconnect ? 'Reconnect Google Calendar' : 'Connect Google Calendar'}
              </Button>
            </Tooltip>
          )}
        </Box>
      </HStack>

      {/* Step 2 — Create the scheduling account */}
      <HStack bg={stepBg} p={4} borderRadius="lg" spacing={4} align="flex-start">
        <Icon as={FiClock} boxSize={6} color={googleReady ? 'blue.400' : 'gray.400'} mt={1} />
        <Box flex="1">
          <Text fontWeight="semibold">2. Create your scheduling account</Text>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Sets up your booking link, two starter meeting types (Intro Call · 15 min,
            Mortgage Consult · 30 min), and Mon–Fri 9–5 availability you can adjust after.
          </Text>
          <Button
            mt={3}
            size="sm"
            colorScheme="purple"
            onClick={handleProvision}
            isLoading={provisioning}
            isDisabled={!googleReady}
            leftIcon={<FiCheckCircle />}
          >
            Create my scheduling account
          </Button>
        </Box>
      </HStack>

      {!status.sidecarReachable && (
        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          The scheduling service isn't reachable right now — try again in a minute.
        </Alert>
      )}
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Meeting Types tab
// ---------------------------------------------------------------------------

const EMPTY_EVENT_FORM = {
  id: null,
  title: '',
  length: 30,
  description: '',
  minimumBookingNotice: 120,
  beforeEventBuffer: 0,
  afterEventBuffer: 0,
};

function MeetingTypesTab({ userId, eventTypes, loading, onReload }) {
  const toast = useToast();
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_EVENT_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const rowBorder = useColorModeValue('gray.100', 'gray.700');

  const openCreate = () => {
    setForm(EMPTY_EVENT_FORM);
    setEditorOpen(true);
  };
  const openEdit = (et) => {
    setForm({
      id: et.id,
      title: et.title || '',
      length: et.length || 30,
      description: et.description || '',
      minimumBookingNotice: et.minimumBookingNotice ?? 120,
      beforeEventBuffer: et.beforeEventBuffer ?? 0,
      afterEventBuffer: et.afterEventBuffer ?? 0,
    });
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.length) {
      toast({ title: 'Name and duration are required', status: 'warning', duration: 3000 });
      return;
    }
    setSaving(true);
    try {
      await call('calSaveEventType', {
        userId,
        eventType: {
          id: form.id || undefined,
          title: form.title.trim(),
          length: Number(form.length),
          description: form.description.trim() || null,
          minimumBookingNotice: Number(form.minimumBookingNotice) || 0,
          beforeEventBuffer: Number(form.beforeEventBuffer) || 0,
          afterEventBuffer: Number(form.afterEventBuffer) || 0,
        },
      });
      toast({ title: form.id ? 'Meeting type updated' : 'Meeting type created', status: 'success', duration: 3000 });
      setEditorOpen(false);
      onReload();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await call('calDeleteEventType', { userId, eventTypeId: deleteTarget.id });
      toast({
        title: 'Meeting type deleted',
        description:
          result.deactivatedPages > 0
            ? `${result.deactivatedPages} booking page(s) that used it were deactivated.`
            : undefined,
        status: 'info',
        duration: 5000,
      });
      setDeleteTarget(null);
      onReload();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Box>
          <Heading size="sm">Meeting Types</Heading>
          <Text fontSize="sm" color={subtleText} mt={1}>
            The kinds of meetings people can book with you. Every meeting gets a Google
            Meet link automatically — Zoom is coming later.
          </Text>
        </Box>
        <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate} flexShrink={0}>
          New Meeting Type
        </Button>
      </HStack>

      {loading ? (
        <Center py={8}><Spinner /></Center>
      ) : eventTypes.length === 0 ? (
        <Center py={8} borderWidth="1px" borderStyle="dashed" borderRadius="lg">
          <Text color={subtleText}>No meeting types yet — create one to get started.</Text>
        </Center>
      ) : (
        <VStack align="stretch" spacing={0} borderWidth="1px" borderRadius="lg" overflow="hidden">
          {eventTypes.map((et, idx) => (
            <HStack
              key={et.id}
              p={4}
              spacing={4}
              borderTopWidth={idx === 0 ? 0 : '1px'}
              borderColor={rowBorder}
              align="center"
            >
              <Icon as={FiVideo} color="purple.400" />
              <Box flex="1">
                <HStack spacing={2}>
                  <Text fontWeight="semibold">{et.title}</Text>
                  <Badge>{et.length} min</Badge>
                </HStack>
                {et.description && (
                  <Text fontSize="sm" color={subtleText} noOfLines={1}>{et.description}</Text>
                )}
                <Text fontSize="xs" color={subtleText}>
                  {et.minimumBookingNotice >= 60
                    ? `${Math.round(et.minimumBookingNotice / 60)}h minimum notice`
                    : `${et.minimumBookingNotice || 0}m minimum notice`}
                  {(et.beforeEventBuffer || et.afterEventBuffer) ?
                    ` · buffers ${et.beforeEventBuffer || 0}m before / ${et.afterEventBuffer || 0}m after` : ''}
                </Text>
              </Box>
              <IconButton size="sm" variant="ghost" icon={<FiEdit2 />} aria-label="Edit" onClick={() => openEdit(et)} />
              <IconButton
                size="sm"
                variant="ghost"
                colorScheme="red"
                icon={<FiTrash2 />}
                aria-label="Delete"
                onClick={() => setDeleteTarget(et)}
              />
            </HStack>
          ))}
        </VStack>
      )}

      {/* Editor modal */}
      <Modal isOpen={editorOpen} onClose={() => setEditorOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{form.id ? 'Edit Meeting Type' : 'New Meeting Type'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Mortgage Checkup"
                />
              </FormControl>
              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Duration (minutes)</FormLabel>
                  <Select
                    value={form.length}
                    onChange={(e) => setForm((p) => ({ ...p, length: Number(e.target.value) }))}
                  >
                    {[15, 20, 30, 45, 60, 90].map((m) => (
                      <option key={m} value={m}>{m} minutes</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Minimum notice</FormLabel>
                  <Select
                    value={form.minimumBookingNotice}
                    onChange={(e) => setForm((p) => ({ ...p, minimumBookingNotice: Number(e.target.value) }))}
                  >
                    <option value={0}>None</option>
                    <option value={60}>1 hour</option>
                    <option value={120}>2 hours</option>
                    <option value={240}>4 hours</option>
                    <option value={1440}>1 day</option>
                  </Select>
                  <FormHelperText fontSize="xs">How close to start time people can book</FormHelperText>
                </FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel fontSize="sm">Description</FormLabel>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                  placeholder="A quick review of your current mortgage and refinance options."
                />
              </FormControl>
              <SimpleGrid columns={2} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Buffer before (min)</FormLabel>
                  <NumberInput
                    min={0}
                    max={120}
                    step={5}
                    value={form.beforeEventBuffer}
                    onChange={(_, v) => setForm((p) => ({ ...p, beforeEventBuffer: Number.isNaN(v) ? 0 : v }))}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Buffer after (min)</FormLabel>
                  <NumberInput
                    min={0}
                    max={120}
                    step={5}
                    value={form.afterEventBuffer}
                    onChange={(_, v) => setForm((p) => ({ ...p, afterEventBuffer: Number.isNaN(v) ? 0 : v }))}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </SimpleGrid>
              <HStack spacing={2} p={3} borderWidth="1px" borderRadius="md" fontSize="sm">
                <Icon as={FiVideo} color="purple.400" />
                <Text>Location: <b>Google Meet</b> — added to every booking automatically</Text>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>
              {form.id ? 'Save Changes' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete confirm */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Meeting Type</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm">
              Delete "{deleteTarget?.title}"? People will no longer be able to book it, and any
              booking pages using it will be deactivated. Existing bookings are not affected.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete} isLoading={deleting}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Availability tab
// ---------------------------------------------------------------------------

function AvailabilityTab({ userId }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timeZone, setTimeZone] = useState('America/New_York');
  // dayConfig: { [dayInt]: { enabled, start, end } }
  const [dayConfig, setDayConfig] = useState({});
  const subtleText = useColorModeValue('gray.600', 'gray.400');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await call('calGetAvailability', { userId });
      const config = {};
      DAYS.forEach((d) => {
        config[d.value] = { enabled: false, start: '09:00', end: '17:00' };
      });
      (result.rules || []).forEach((rule) => {
        (rule.days || []).forEach((day) => {
          config[day] = {
            enabled: true,
            start: toInputTime(rule.startTime),
            end: toInputTime(rule.endTime),
          };
        });
      });
      setDayConfig(config);
      if (result.timeZone) setTimeZone(result.timeZone);
    } catch (err) {
      toast({ title: 'Failed to load availability', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    // Group enabled days with identical windows into shared rules
    const groups = new Map();
    DAYS.forEach((d) => {
      const cfg = dayConfig[d.value];
      if (!cfg?.enabled) return;
      if (!cfg.start || !cfg.end || cfg.start >= cfg.end) return; // skip invalid rows
      const key = `${cfg.start}|${cfg.end}`;
      if (!groups.has(key)) groups.set(key, { days: [], startTime: `${cfg.start}:00`, endTime: `${cfg.end}:00` });
      groups.get(key).days.push(d.value);
    });
    const rules = Array.from(groups.values());
    if (!rules.length) {
      toast({ title: 'Pick at least one day', description: 'Enable at least one day with a valid time window.', status: 'warning', duration: 4000 });
      return;
    }
    setSaving(true);
    try {
      await call('calSaveAvailability', { userId, timeZone, rules });
      toast({ title: 'Availability saved', status: 'success', duration: 3000 });
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Center py={8}><Spinner /></Center>;

  return (
    <Box maxW="560px">
      <Heading size="sm" mb={1}>Weekly Hours</Heading>
      <Text fontSize="sm" color={subtleText} mb={4}>
        When people can book you. Your Google Calendar busy times are excluded automatically.
      </Text>

      <FormControl mb={4} maxW="320px">
        <FormLabel fontSize="sm">Timezone</FormLabel>
        <Select value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
          {TIMEZONES.map((tz) => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
          {!TIMEZONES.some((tz) => tz.value === timeZone) && (
            <option value={timeZone}>{timeZone}</option>
          )}
        </Select>
      </FormControl>

      <VStack align="stretch" spacing={2} mb={5}>
        {DAYS.map((d) => {
          const cfg = dayConfig[d.value] || { enabled: false, start: '09:00', end: '17:00' };
          return (
            <HStack key={d.value} spacing={4}>
              <Switch
                isChecked={cfg.enabled}
                onChange={(e) =>
                  setDayConfig((p) => ({ ...p, [d.value]: { ...cfg, enabled: e.target.checked } }))
                }
              />
              <Text w="100px" fontSize="sm" fontWeight={cfg.enabled ? 'semibold' : 'normal'}>
                {d.label}
              </Text>
              {cfg.enabled ? (
                <HStack spacing={2}>
                  <Input
                    type="time"
                    size="sm"
                    w="110px"
                    value={cfg.start}
                    onChange={(e) =>
                      setDayConfig((p) => ({ ...p, [d.value]: { ...cfg, start: e.target.value } }))
                    }
                  />
                  <Text fontSize="sm" color={subtleText}>to</Text>
                  <Input
                    type="time"
                    size="sm"
                    w="110px"
                    value={cfg.end}
                    onChange={(e) =>
                      setDayConfig((p) => ({ ...p, [d.value]: { ...cfg, end: e.target.value } }))
                    }
                  />
                </HStack>
              ) : (
                <Text fontSize="sm" color={subtleText}>Unavailable</Text>
              )}
            </HStack>
          );
        })}
      </VStack>

      <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>
        Save Availability
      </Button>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Bookings tab
// ---------------------------------------------------------------------------

function BookingsTab({ userId, timeZoneHint }) {
  const toast = useToast();
  const [scope, setScope] = useState('upcoming');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [noShowTarget, setNoShowTarget] = useState(null);
  const [markingNoShow, setMarkingNoShow] = useState(false);
  const subtleText = useColorModeValue('gray.600', 'gray.400');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await call('calListBookings', { userId, scope, limit: 50 });
      setBookings(result.bookings || []);
    } catch (err) {
      toast({ title: 'Failed to load bookings', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [userId, scope, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await call('calCancelBooking', {
        userId,
        bookingUid: cancelTarget.uid,
        reason: cancelReason.trim() || 'Cancelled by your loan officer',
      });
      toast({
        title: 'Booking cancelled',
        description: 'The attendee gets a cancellation email, and your CRM timeline updates automatically.',
        status: 'success',
        duration: 5000,
      });
      setCancelTarget(null);
      setCancelReason('');
      load();
    } catch (err) {
      toast({ title: 'Cancel failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setCancelling(false);
    }
  };

  const handleMarkNoShow = async () => {
    if (!noShowTarget) return;
    setMarkingNoShow(true);
    try {
      await call('calMarkNoShow', { userId, bookingUid: noShowTarget.uid });
      toast({
        title: 'Marked as no-show',
        description:
          'Logged on the contact\'s timeline. If you\'re subscribed to the "Meeting No-Show Rebook" campaign, the rebook follow-up goes out automatically.',
        status: 'success',
        duration: 6000,
      });
      setNoShowTarget(null);
      load();
    } catch (err) {
      toast({ title: 'Could not mark no-show', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setMarkingNoShow(false);
    }
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return <Badge colorScheme="green">Confirmed</Badge>;
      case 'cancelled':
        return <Badge colorScheme="red">Cancelled</Badge>;
      case 'pending':
        return <Badge colorScheme="yellow">Pending</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Box>
          <Heading size="sm">Bookings</Heading>
          <Text fontSize="sm" color={subtleText} mt={1}>
            Meetings booked through your pages. Cancelling here emails the attendee and
            logs it on the contact's timeline.
          </Text>
        </Box>
        <ButtonGroup size="sm" isAttached variant="outline">
          <Button isActive={scope === 'upcoming'} onClick={() => setScope('upcoming')}>Upcoming</Button>
          <Button isActive={scope === 'past'} onClick={() => setScope('past')}>Past</Button>
        </ButtonGroup>
      </HStack>

      {loading ? (
        <Center py={8}><Spinner /></Center>
      ) : bookings.length === 0 ? (
        <Center py={8} borderWidth="1px" borderStyle="dashed" borderRadius="lg">
          <Text color={subtleText}>No {scope} bookings.</Text>
        </Center>
      ) : (
        <Box overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>When</Th>
                <Th>Meeting</Th>
                <Th>Attendee</Th>
                <Th>Status</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {bookings.map((b) => {
                const attendee = (b.attendees || [])[0] || {};
                return (
                  <Tr key={b.uid}>
                    <Td whiteSpace="nowrap">{formatBookingTime(b.startTime, timeZoneHint)}</Td>
                    <Td>
                      <Text fontWeight="medium" noOfLines={1}>{b.eventType?.title || b.title}</Text>
                      {b.cancellationReason && (
                        <Text fontSize="xs" color={subtleText} noOfLines={1}>
                          Reason: {b.cancellationReason}
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <Text noOfLines={1}>{attendee.name || '—'}</Text>
                      <Text fontSize="xs" color={subtleText} noOfLines={1}>
                        {attendee.email}{attendee.phoneNumber || b.phone ? ` · ${attendee.phoneNumber || b.phone}` : ''}
                      </Text>
                    </Td>
                    <Td>
                      {statusBadge(b.status)}
                      {attendee.noShow && (
                        <Badge colorScheme="orange" ml={1}>No-show</Badge>
                      )}
                    </Td>
                    <Td textAlign="right" whiteSpace="nowrap">
                      {b.meetingUrl && (
                        <Tooltip label="Open Google Meet link">
                          <IconButton
                            as={Link}
                            href={b.meetingUrl}
                            isExternal
                            size="xs"
                            variant="ghost"
                            icon={<FiVideo />}
                            aria-label="Meet link"
                          />
                        </Tooltip>
                      )}
                      {b.status === 'accepted' && scope === 'past' && !attendee.noShow && (
                        <Tooltip label="Mark as no-show">
                          <IconButton
                            size="xs"
                            variant="ghost"
                            colorScheme="orange"
                            icon={<FiUserX />}
                            aria-label="Mark as no-show"
                            onClick={() => setNoShowTarget(b)}
                          />
                        </Tooltip>
                      )}
                      {b.status !== 'cancelled' && scope === 'upcoming' && (
                        <Tooltip label="Cancel booking">
                          <IconButton
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            icon={<FiXCircle />}
                            aria-label="Cancel booking"
                            onClick={() => setCancelTarget(b)}
                          />
                        </Tooltip>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Cancel dialog */}
      <Modal isOpen={!!cancelTarget} onClose={() => setCancelTarget(null)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Cancel Booking</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm">
                Cancel "{cancelTarget?.eventType?.title || cancelTarget?.title}" with{' '}
                <b>{(cancelTarget?.attendees || [])[0]?.name || 'the attendee'}</b> on{' '}
                {formatBookingTime(cancelTarget?.startTime, timeZoneHint)}?
              </Text>
              <FormControl>
                <FormLabel fontSize="sm">Reason (sent to the attendee)</FormLabel>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={2}
                  placeholder="Something came up — I'll follow up to find a new time."
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setCancelTarget(null)}>Keep Booking</Button>
            <Button colorScheme="red" onClick={handleCancel} isLoading={cancelling}>
              Cancel Booking
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Mark no-show dialog */}
      <Modal isOpen={!!noShowTarget} onClose={() => setNoShowTarget(null)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mark as No-Show</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm">
                Mark <b>{(noShowTarget?.attendees || [])[0]?.name || 'the attendee'}</b> as a no-show
                for "{noShowTarget?.eventType?.title || noShowTarget?.title}" on{' '}
                {formatBookingTime(noShowTarget?.startTime, timeZoneHint)}?
              </Text>
              <Text fontSize="sm" color="orange.500">
                This logs the no-show on their timeline and kicks off your rebook follow-up
                (a same-day email with your booking link, plus a text nudge in two days if you
                have Dialpad). It can't be unsent, so double-check they really didn't show.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setNoShowTarget(null)}>Back</Button>
            <Button colorScheme="orange" onClick={handleMarkNoShow} isLoading={markingNoShow}>
              Mark No-Show
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function SchedulingIntegration({ userId, targetUser, isAdmin }) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [eventTypes, setEventTypes] = useState([]);
  const [eventTypesLoading, setEventTypesLoading] = useState(false);
  const eventTypesLoadedFor = useRef(null);

  const isSelf = userId === currentUser?.uid;
  const enabled = targetUser?.calSchedulingEnabled === true;
  const subtleText = useColorModeValue('gray.600', 'gray.400');

  const bookingUrl = status?.defaultBookingUrl || targetUser?.defaultBookingUrl || '';
  const { onCopy, hasCopied } = useClipboard(bookingUrl);

  const loadStatus = useCallback(async () => {
    if (!enabled) {
      setStatusLoading(false);
      return;
    }
    setStatusLoading(true);
    try {
      const result = await call('calGetIntegrationStatus', { userId });
      setStatus(result);
    } catch (err) {
      console.error('[SchedulingIntegration] status load failed:', err);
      toast({ title: 'Failed to load scheduling status', description: err.message, status: 'error', duration: 5000 });
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, [userId, enabled, toast]);

  const loadEventTypes = useCallback(async () => {
    setEventTypesLoading(true);
    try {
      const result = await call('calListEventTypes', { userId });
      setEventTypes(result.eventTypes || []);
    } catch (err) {
      console.error('[SchedulingIntegration] event types load failed:', err);
      setEventTypes([]);
    } finally {
      setEventTypesLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setStatus(null);
    setEventTypes([]);
    eventTypesLoadedFor.current = null;
    loadStatus();
  }, [loadStatus]);

  const provisioned = !!status?.provisioned;

  // Load event types once provisioned (they feed both the Meeting Types tab
  // and the booking-page meeting-type dropdown)
  useEffect(() => {
    if (provisioned && eventTypesLoadedFor.current !== userId) {
      eventTypesLoadedFor.current = userId;
      loadEventTypes();
    }
  }, [provisioned, userId, loadEventTypes]);

  if (!enabled) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="semibold">FHL Scheduling isn't enabled for {isSelf ? 'you' : 'this user'} yet.</Text>
          <Text fontSize="sm">
            {isAdmin
              ? 'Set calSchedulingEnabled: true on their user document to add them to the pilot.'
              : 'Ask an admin to add you to the scheduling pilot.'}
          </Text>
        </Box>
      </Alert>
    );
  }

  if (statusLoading) {
    return <Center py={10}><Spinner size="lg" /></Center>;
  }

  if (!status) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <HStack justify="space-between" flex="1">
          <Text fontSize="sm">Couldn't reach the scheduling service.</Text>
          <Button size="sm" leftIcon={<FiRefreshCw />} onClick={loadStatus}>Retry</Button>
        </HStack>
      </Alert>
    );
  }

  if (!provisioned) {
    return <SetupChecklist userId={userId} isSelf={isSelf} status={status} onDone={loadStatus} />;
  }

  return (
    <Box>
      {/* Header: booking link + copy */}
      <HStack justify="space-between" mb={4} flexWrap="wrap" gap={3}>
        <Box>
          <Heading size="md">FHL Scheduling</Heading>
          <Text fontSize="sm" color={subtleText}>
            Active as <b>{status.bookingHandle}</b>
            {status.google?.needsReconnect && ' — Google needs a reconnect for availability checks'}
          </Text>
        </Box>
        {bookingUrl && (
          <InputGroup maxW="440px" size="sm">
            <Input value={bookingUrl} isReadOnly borderRadius="md" pr="4.5rem" />
            <InputRightElement width="4.5rem">
              <HStack spacing={0}>
                <Tooltip label={hasCopied ? 'Copied!' : 'Copy booking link'}>
                  <IconButton size="xs" variant="ghost" icon={<FiCopy />} aria-label="Copy" onClick={onCopy} />
                </Tooltip>
                <Tooltip label="Open">
                  <IconButton
                    as={Link}
                    href={bookingUrl}
                    isExternal
                    size="xs"
                    variant="ghost"
                    icon={<FiExternalLink />}
                    aria-label="Open"
                  />
                </Tooltip>
              </HStack>
            </InputRightElement>
          </InputGroup>
        )}
      </HStack>

      {status.google?.needsReconnect && (
        <Alert status="warning" borderRadius="md" mb={4} fontSize="sm">
          <AlertIcon />
          Google Calendar needs one extra permission for availability checks — reconnect it from
          the Google Calendar card above (takes 10 seconds).
        </Alert>
      )}

      <Divider mb={4} />

      <Tabs colorScheme="purple" isLazy variant="soft-rounded" size="sm">
        <TabList flexWrap="wrap" gap={1}>
          <Tab>Booking Pages</Tab>
          <Tab>Meeting Types</Tab>
          <Tab>Availability</Tab>
          <Tab>Bookings</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0} pt={5}>
            <BookingPagesManager
              userId={userId}
              targetUser={targetUser}
              meetingTypes={eventTypes}
              calUsername={status.bookingHandle || targetUser?.bookingHandle || ''}
            />
          </TabPanel>
          <TabPanel px={0} pt={5}>
            <MeetingTypesTab
              userId={userId}
              eventTypes={eventTypes}
              loading={eventTypesLoading}
              onReload={loadEventTypes}
            />
          </TabPanel>
          <TabPanel px={0} pt={5}>
            <AvailabilityTab userId={userId} />
          </TabPanel>
          <TabPanel px={0} pt={5}>
            <BookingsTab userId={userId} timeZoneHint={status.cal?.timeZone} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
